import React, { useState, useMemo, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import useVolunteerKyc from "../../../Hooks/Volunteer Api/useVolunteerKyc";

const ViewVolunteerKycDetails = ({
  baseurl,
  token,
  selectedVolunteer,
  setSelectedVolunteer,
  setKycVolunteers,
  activeTab,
  previewImage,
  setPreviewImage,
  openKycDialogId,
  setOpenKycDialogId,
  AdminId,
}) => {
  const [volunteerKycStatus, setLocalVolunteerKycStatus] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

  const {
    volunteerKyc,
    fetchVolunteerKycDetails,
    handleVolunteerKycAction,
    kycLoading,
  } = useVolunteerKyc();

  const kycId = volunteerKyc?.id || null;

  const images = useMemo(() => {
    if (!volunteerKyc) return [];

    return [
      {
        src: volunteerKyc.profile_photo,
        label: "Profile Photo",
      },
      {
        src: volunteerKyc.aadhar_front,
        label: "Aadhaar Front",
      },
      {
        src: volunteerKyc.aadhar_back,
        label: "Aadhaar Back",
      },
      {
        src: volunteerKyc.pan_front,
        label: "PAN Card Front",
      },
    ];
  }, [volunteerKyc]);

  const closeModel = useCallback(() => {
    setOpenKycDialogId(null);
    setSelectedVolunteer(null);
    setPreviewImage(null);
    setLocalVolunteerKycStatus(null);
  }, [setOpenKycDialogId, setSelectedVolunteer, setPreviewImage]);

  const handleImageClick = useCallback(
    (imageSrc) => {
      setPreviewImage(`${imageSrc}?t=${Date.now()}`);
    },
    [setPreviewImage],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewImage(null);
  }, [setPreviewImage]);

  const handleKycStatusUpdate = async (kycId, statusCode) => {
    setLoadingAction(statusCode === "A" ? "Approve" : "Reject");

    try {
      const result = await handleVolunteerKycAction(
        kycId,
        "update",
        statusCode,
      );

      if (result?.success) {
        toast.success("KYC updated successfully!");
        fetchVolunteerKycDetails(selectedVolunteer.volunteer_code, false);
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const statusInfo = useMemo(() => {
    const rawStatus = volunteerKyc?.kycStatus ?? volunteerKyc?.status ?? "";

    const normalized = rawStatus.toString().toUpperCase();

    return {
      rawStatus: normalized,
      isApproved: normalized === "A",
      isRejected: normalized === "R",
    };
  }, [volunteerKyc?.kycStatus, volunteerKyc?.status]);
  console.log("volunteerKyc:", volunteerKyc);

  useEffect(() => {
    if (selectedVolunteer?.volunteer_code) {
      fetchVolunteerKycDetails(selectedVolunteer.volunteer_code);
    }
  }, [selectedVolunteer?.volunteer_code]);

  return (
    <div className="p-4 border rounded-md shadow-sm bg-white space-y-4">
      {openKycDialogId && selectedVolunteer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModel}
        >
          <div
            className="bg-white rounded-lg shadow-md max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">KYC Details</h2>
              <button
                onClick={closeModel}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Volunteer Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Volunteer Code:
                      </span>{" "}
                      {volunteerKyc?.id || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Name:</span>{" "}
                      {volunteerKyc?.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Email:</span>{" "}
                      {volunteerKyc?.email || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Volunteer Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Phone:</span>{" "}
                      {volunteerKyc?.phone || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Aadhaar No:
                      </span>{" "}
                      {volunteerKyc?.aadhar_no || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">PAN No:</span>{" "}
                      {volunteerKyc?.pan_no || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {images.map((item, index) => (
                  <div key={index} className="relative text-center">
                    {item.src && item.src !== "" && item.src !== "No URL" ? (
                      <div className="relative group">
                        <img
                          src={item.src}
                          alt={item.label}
                          className="w-full h-48 object-cover rounded mb-2 cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => handleImageClick(item.src)}
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://via.placeholder.com/300x200?text=No+Image";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded mb-2 flex flex-col items-center justify-center text-sm text-gray-600">
                        <p className="font-medium">No Image</p>
                      </div>
                    )}
                    <h5 className="font-medium text-gray-800 mt-1">
                      {item.label}
                    </h5>
                  </div>
                ))}
              </div>

              {/* Preview Modal */}
              {previewImage && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                  onClick={handlePreviewClose}
                >
                  <div
                    className="relative bg-white p-2 rounded-lg shadow-xl max-w-4xl max-h-[75vh]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="max-w-full max-h-[65vh] object-contain rounded"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/600x500?text=No+Image";
                      }}
                    />
                    <button
                      onClick={handlePreviewClose}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 hover:bg-gray-100"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-center gap-4">
                {!statusInfo.isApproved && (
                  <button
                    className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600"
                    onClick={() => handleKycStatusUpdate(kycId, "Approve")}
                    disabled={loadingAction === "Approve" || !kycId}
                  >
                    {loadingAction === "Approve" ? "Processing..." : "Approve"}
                  </button>
                )}

                {!statusInfo.isRejected && AdminId==="3" && AdminId==="13" && (
                  <button
                    className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600"
                    onClick={() => handleKycStatusUpdate(kycId, "Reject")}
                    disabled={loadingAction === "Reject" || !kycId}
                  >
                    {loadingAction === "Reject" ? "Processing..." : "Reject"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewVolunteerKycDetails;
